import React, { useState } from 'react';
import { FileText } from 'lucide-react';
import { API_ENDPOINTS } from '../../config/api';
import { t } from '../../i18n/languages';

const GenerateReportModal = ({ isOpen, onClose, currentMonth, currentLanguage }) => {
  const [selectedFormat, setSelectedFormat] = useState('per_day');
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const downloadReport = async () => {
    setLoading(true);
    try {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth() + 1;
      const monthName = new Intl.DateTimeFormat(
        currentLanguage === 'hr' ? 'hr-HR' : 'en-US',
        { month: 'long' }
      ).format(currentMonth);

      const url = `${API_ENDPOINTS.MONTHLY_REPORT_DOWNLOAD}?year=${year}&month=${month}&lang=${currentLanguage}&format=${selectedFormat}`;

      const response = await fetch(url, {
        credentials: 'include',
      });

      if (response.ok) {
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename;
        if (selectedFormat === 'per_day') {
          filename = currentLanguage === 'hr'
            ? `evidencija_${monthName}.xlsx`
            : `report_${monthName}.xlsx`;
        } else if (selectedFormat === 'cummulative') {
          filename = currentLanguage === 'hr'
            ? `mjesečni_izvještaj_radne_evidencije_${month}_${year}_kumulativno.xlsx`
            : `monthly_report_${month}_${year}_cummulative.xlsx`;
        } else {
          throw new Error(`Invalid format selected: ${selectedFormat}`);
        }

        if (contentDisposition) {
          const match = contentDisposition.match(/filename="(.+)"/);
          if (match) filename = match[1];
        }

        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
        onClose();
      } else {
        const errorData = await response.json();
        alert('Failed to download report: ' + (errorData.error || 'Unknown error'));
      }
    } catch (err) {
      alert('Network error while downloading report');
    }
    setLoading(false);
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white', borderRadius: '12px', padding: '24px',
        maxWidth: '400px', width: '90%', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <div style={{
            width: '48px', height: '48px', borderRadius: '50%',
            backgroundColor: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <FileText size={24} color="#3b82f6" />
          </div>
          <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#1f2937' }}>
            {t('selectReportFormat', {}, currentLanguage)}
          </h3>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label style={{
            display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px'
          }}>
            {currentLanguage === 'hr' ? 'Format' : 'Format'}
          </label>
          <select
            value={selectedFormat}
            onChange={(e) => setSelectedFormat(e.target.value)}
            style={{
              width: '100%', padding: '10px 12px', border: '1px solid #d1d5db',
              borderRadius: '6px', fontSize: '14px'
            }}
          >
            <option value="per_day">{t('perDay', {}, currentLanguage)}</option>
            <option value="cummulative">{t('cummulative', {}, currentLanguage)}</option>
          </select>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '10px 20px', backgroundColor: 'transparent', color: '#6b7280',
              border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px',
              fontWeight: '500', cursor: 'pointer', transition: 'all 0.2s'
            }}
          >
            {t('cancel', {}, currentLanguage)}
          </button>
          <button
            type="button"
            onClick={downloadReport}
            disabled={loading}
            style={{
              padding: '10px 20px', backgroundColor: '#3b82f6', color: 'white',
              border: 'none', borderRadius: '8px', fontSize: '14px',
              fontWeight: '500', cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1, transition: 'background-color 0.2s'
            }}
          >
            {loading ? (currentLanguage === 'hr' ? 'Preuzimanje...' : 'Downloading...') : (t('download', {}, currentLanguage))}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GenerateReportModal;
